import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { ConfigService, getToolGroups, ToolItem } from '@web-client/shared';

@Component({
  selector: 'lib-suite-landing',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule],
  styles: [`
    :host { display: block; }
    .home-wrapper { background: var(--mat-sys-surface-container-lowest, #fff); min-height: 100vh; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 1.5rem; }
    .hero-section { padding: 3rem 0 1.5rem; text-align: left; }
    .hero-title { font-size: 1.75rem; font-weight: 700; letter-spacing: -0.01em; margin-bottom: 0.5rem; color: var(--mat-sys-on-surface); }
    .hero-description { font-size: 0.95rem; color: var(--mat-sys-on-surface-variant); max-width: 600px; line-height: 1.5; opacity: 0.8; }
    .tools-section { padding: 2.5rem 0 5rem; }
    .tools-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 0.75rem; }
    .tool-card {
        display: flex; align-items: center; padding: 1rem; 
        background: var(--mat-sys-surface-container-low); border: 1px solid var(--mat-sys-outline-variant);
        border-radius: 12px; text-decoration: none; color: inherit; transition: all 0.2s ease;
    }
    .tool-card:hover {
        background: var(--mat-sys-surface-container-high); border-color: var(--tool-accent);
        box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.05);
    }
    .tool-card:hover .tool-icon-box { background: var(--tool-accent); color: white; transform: scale(1.05) rotate(2deg); }
    .tool-icon-box {
        width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
        background: var(--mat-sys-surface-container-highest); color: var(--tool-accent);
        border-radius: 10px; margin-right: 1.125rem; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .tool-icon-box mat-icon { font-size: 22px; width: 22px; height: 22px; }
    .tool-info { flex: 1; display: flex; flex-direction: column; }
    .tool-name { margin: 0; font-size: 1.05rem; font-weight: 400; color: var(--mat-sys-on-surface); }
    .tool-parent { font-size: 0.77rem; color: var(--mat-sys-on-surface-variant); opacity: 0.6; }
    .launch-icon { font-size: 16px; width: 16px; height: 16px; color: var(--mat-sys-primary); opacity: 0; transition: opacity 0.2s ease; }
    .tool-card:hover .launch-icon { opacity: 0.4; }
    .coming-soon-badge {
        font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
        padding: 2px 6px; margin-top: 0.25rem; background: #fffbeb; color: #b45309;
        border: 1px solid #fde68a; border-radius: 4px; align-self: flex-start;
    }
    :host-context(.dark-mode) .coming-soon-badge { background: rgba(251, 191, 36, 0.1); color: #fbbf24; border-color: rgba(251, 191, 36, 0.2); }
  `],
  template: `
    <main class="home-wrapper">
      <section class="hero-section">
        <div class="container">
          <h1 class="hero-title">{{ title }}</h1>
          <p class="hero-description">{{ description }}</p>
        </div>
      </section>

      <section class="tools-section">
        <div class="container">
          <div class="tools-grid">
            <a *ngFor="let tool of tools" 
               [routerLink]="tool.path"
               class="tool-card group"
               [style.--tool-accent]="tool.color || themeColor">
              
              <div class="tool-icon-box">
                <mat-icon>{{ tool.icon }}</mat-icon>
              </div>

              <div class="tool-info">
                <h3 class="tool-name">{{ tool.name }}</h3>
                <p class="tool-parent">{{ tool.description }}</p>
                <span *ngIf="tool.comingSoon" class="coming-soon-badge">Soon</span>
              </div>
              
              <mat-icon class="launch-icon">arrow_forward</mat-icon>
            </a>
          </div>
        </div>
      </section>
    </main>
  `
})
export class SuiteLandingComponent implements OnInit {
  private readonly configService = inject(ConfigService);
  get appName(): string {
    return this.configService.appName;
  }

  @Input() rootPath = '';
  @Input() title = '';
  @Input() description = '';
  @Input() themeColor = 'var(--mat-sys-primary)';
  @Input() ribbonColorClass = 'bg-primary';
  @Input() defaultIconClass = 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';

  tools: ToolItem[] = [];
  firstToolPath = '';

  ngOnInit() {
    const groups = getToolGroups(this.rootPath);
    this.tools = groups.flatMap(g => g.items).filter(i => i.path !== './');
    if (this.tools.length > 0) {
      this.firstToolPath = this.tools[0].path;
    }
  }
}
